import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, Timestamp } from "firebase/firestore"
import { db } from "@/lib/firebase"

/**
 * Check if a user has had a completed booking with an artist
 * (booking with scheduledTimestamp in the past)
 */
export async function checkUserHasBookingWithArtist(
  userId: string,
  artistId: string
): Promise<boolean> {
  try {
    const bookingsRef = collection(db, 'bookings')
    const bookingsQuery = query(
      bookingsRef,
      where('userId', '==', userId),
      where('artistId', '==', artistId)
    )
    
    const snapshot = await getDocs(bookingsQuery)
    
    // Check if any booking has a scheduledTimestamp in the past
    const now = new Date()
    const hasPastBooking = snapshot.docs.some(doc => {
      const data = doc.data()
      const scheduledTimestamp = data.scheduledTimestamp instanceof Timestamp 
        ? data.scheduledTimestamp.toDate() 
        : new Date(data.scheduledTimestamp)
      
      return scheduledTimestamp < now
    })
    
    return hasPastBooking
  } catch (error) {
    console.error('[reviewData] Error checking user booking status:', error)
    // If there's a permission error or other issue, default to false (not verified)
    return false
  }
}

export interface Review {
  id: string
  artistId: string
  userId: string
  starRating: number
  reviewText: string
  reviewUrls?: string[]
  verificationUrls?: string[] // Photos for verification only (not displayed on review)
  username?: string
  userAvatarUrl?: string
  isAnonymous: boolean
  isVerifiedClient: boolean // User has had a booking with this artist
  isApproved: boolean // Review has been approved by admin/artist
  createdAt: Date
}

/**
 * Fetch approved reviews for a specific artist, ordered by most recent first.
 * Pending / unapproved reviews are omitted (public display only).
 */
export async function getReviewsByArtistId(artistId: string): Promise<Review[]> {
  try {
    const reviewsQuery = query(
      collection(db, 'reviews'),
      where('artistId', '==', artistId),
      orderBy('createdAt', 'desc')
    )
    
    const snapshot = await getDocs(reviewsQuery)
    const reviews = snapshot.docs.map(doc => {
      const data = doc.data()
      return {
        id: doc.id,
        artistId: data.artistId,
        userId: data.userId,
        starRating: data.starRating,
        reviewText: data.reviewText,
        reviewUrls: data.reviewUrls,
        verificationUrls: data.verificationUrls,
        username: data.username,
        userAvatarUrl: data.userAvatarUrl,
        isAnonymous: data.isAnonymous,
        isVerifiedClient: data.isVerifiedClient ?? false,
        isApproved: data.isApproved ?? false, // Default to false - reviews must be explicitly approved
        createdAt: data.createdAt instanceof Timestamp 
          ? data.createdAt.toDate() 
          : new Date(data.createdAt)
      } as Review
    })
    
    return reviews.filter((r) => r.isApproved === true)
  } catch (error) {
    console.error('[reviewData] Error fetching reviews for artist:', artistId, error)
    return []
  }
}

/**
 * Submit a new review for an artist
 */
export async function submitReview(
  artistId: string,
  userId: string,
  rating: number,
  reviewText: string,
  options?: {
    username?: string
    userAvatarUrl?: string
    isAnonymous?: boolean
    reviewUrls?: string[]
    verificationUrls?: string[]
    isVerifiedClient?: boolean
    isApproved?: boolean
  }
): Promise<string> {
  if (!artistId || !userId || rating === 0 || !reviewText.trim()) {
    throw new Error('Missing required fields for review submission')
  }

  try {
    const isAnon = options?.isAnonymous ?? false
    const reviewData: any = {
      artistId,
      userId,
      starRating: rating,
      reviewText: reviewText.trim(),
      createdAt: serverTimestamp(),
      isAnonymous: isAnon,
      reviewUrls: options?.reviewUrls ?? [],
      verificationUrls: options?.verificationUrls ?? [],
      isVerifiedClient: options?.isVerifiedClient ?? false,
      isApproved: options?.isApproved ?? false
    }

    // Only add username if not anonymous. Omit userAvatarUrl when missing — Firestore rejects
    // undefined; ReviewCard shows the same default User icon as anonymous when no URL is stored.
    if (!isAnon) {
      reviewData.username = options?.username ?? 'User'
      const avatarUrl = options?.userAvatarUrl
      if (avatarUrl) {
        reviewData.userAvatarUrl = avatarUrl
      }
    }

    const docRef = await addDoc(collection(db, 'reviews'), reviewData)
    console.log('[reviewData] Review submitted successfully with ID:', docRef.id)
    return docRef.id
  } catch (error) {
    console.error('[reviewData] Error submitting review:', error)
    throw error
  }
}

/**
 * Get average rating for an artist
 */
export async function getArtistAverageRating(artistId: string): Promise<number | null> {
  try {
    const reviews = await getReviewsByArtistId(artistId)
    
    if (reviews.length === 0) {
      return null
    }

    const totalRating = reviews.reduce((sum, review) => sum + review.starRating, 0)
    const averageRating = totalRating / reviews.length
    
    return Math.round(averageRating * 10) / 10 // Round to 1 decimal place
  } catch (error) {
    console.error('[reviewData] Error calculating average rating:', error)
    return null
  }
}

/**
 * Get rating distribution for an artist (count of reviews by star rating)
 */
export async function getArtistRatingDistribution(
  artistId: string
): Promise<Record<number, number>> {
  try {
    const reviews = await getReviewsByArtistId(artistId)
    
    const distribution: Record<number, number> = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    }

    reviews.forEach(review => {
      if (review.starRating >= 1 && review.starRating <= 5) {
        distribution[review.starRating]++
      }
    })

    return distribution
  } catch (error) {
    console.error('[reviewData] Error calculating rating distribution:', error)
    return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  }
}

/**
 * Get rating statistics for an artist
 */
export async function getArtistRatingStats(artistId: string) {
  try {
    const [reviews, averageRating, distribution] = await Promise.all([
      getReviewsByArtistId(artistId),
      getArtistAverageRating(artistId),
      getArtistRatingDistribution(artistId)
    ])

    return {
      totalReviews: reviews.length,
      averageRating,
      distribution,
      reviews
    }
  } catch (error) {
    console.error('[reviewData] Error getting rating stats:', error)
    return {
      totalReviews: 0,
      averageRating: null,
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      reviews: []
    }
  }
}
